import React from 'react';
import { createEvent, sample } from 'effector';
import { parseJsonString } from 'pano-utils';
import { nanoid } from 'nanoid';
import {
  type LibItemMatrix,
  type LibItemPano,
  type LibItemPVideo,
  type LibItemScene,
  LibType,
  type MatrixFov,
  type PanoMultires,
  SceneType,
  type TranscodeTrack,
} from '@types';
import { VIP_URL } from '@constants';
import { sendWarning } from '@components/Noti';
import { showConfirmModal } from '@components/ConfirmModal';
import {
  $categoryMapApi,
  $sceneMap,
  $sceneMapApi,
  $selectedPath,
  $selectedPathApi,
  $tourData,
} from '@stores/dataStores';
import { getMaxSceneNumber } from '@utils/getMaxSceneNumber';
import { getLibPVideoData } from '@apis/mediaApis';
import { selectLibScenes } from '../../mediaLib';
import { viewDefault } from '../../view';

const addScenes = createEvent<number | undefined>();

const getType = (libType: LibType) => {
  if (libType === LibType.panoVideo) return SceneType.panoVideo;
  if (libType === LibType.matrix) return SceneType.matrix;
  return SceneType.pano;
};

// const WARNING = 50;

sample({
  source: {
    selectedPath: $selectedPath,
    tour: $tourData,
    sceneMap: $sceneMap,
  },
  clock: addScenes,
  fn: (source, targetCategory) => ({ ...source, targetCategory }),
}).watch(async ({ selectedPath, tour, sceneMap, targetCategory }) => {
  const { level, maxScene } = getMaxSceneNumber();
  if (level >= 3 && sceneMap.size >= maxScene) {
    await showConfirmModal({
      title: '添加失败',
      buttonText: '我知道了',
      content: (
        <>
          <div className="mb-10 text-xl">已超过最大可添加的场景数</div>
          <div>
            {`每个作品最多支持添加${maxScene}个场景，添加过多场景将影响作品打开速度，甚至打开失效`}
          </div>
        </>
      ),
    });
    return;
  }
  if (level < 3 && sceneMap.size >= maxScene) {
    const { confirmed } = await showConfirmModal({
      title: '温馨提示',
      buttonText: '去升级',
      cancelText: '取消升级',
      content: (
        <>
          <div className="mb-10 text-xl">
            场景数量已超过当前版本最大可添加的场景数
          </div>
          <div>升级会员后可以继续添加更多场景</div>
        </>
      ),
    });
    if (confirmed) {
      window.open(VIP_URL, '_blank');
    }
    return;
  }
  // if (sceneMap.size > WARNING) {
  //   const { confirmed } = await showConfirmModal({
  //     title: '温馨提示',
  //     buttonText: '继续添加',
  //     content: (
  //       <>
  //         <div>{`场景数量已超过${WARNING}个`}</div>
  //         <div>添加过多场景将影响作品打开速度，甚至打开失效</div>
  //       </>
  //     ),
  //   });
  //   if (!confirmed) return;
  // }

  const addedIds = {
    [LibType.pano]: new Set<number>(),
    [LibType.panoVideo]: new Set<number>(),
    [LibType.matrix]: new Set<number>(),
  };

  [...sceneMap.values()].forEach((x) => {
    if (x.type === SceneType.pano) {
      addedIds[LibType.pano].add(x.materialId);
    } else if (x.type === SceneType.panoVideo) {
      addedIds[LibType.panoVideo].add(x.materialId);
    } else if (x.type === SceneType.matrix) {
      addedIds[LibType.matrix].add(x.materialId);
    }
  });

  const { panos, panoVideos, matrices } = await selectLibScenes({
    nav: [LibType.pano, LibType.panoVideo, LibType.matrix],
    addedIds,
    multiple: true,
  });

  const items = panos || panoVideos || matrices;
  if (!items) return;
  if (items.length + sceneMap.size > maxScene) {
    sendWarning('场景数超过上限，部分素材未添加成功');
    items.length = maxScene - sceneMap.size;
  }
  const type = (() => {
    if (panoVideos) return LibType.panoVideo;
    if (matrices) return LibType.matrix;
    return LibType.pano;
  })();

  const originTrackList: (string | undefined)[] = [];
  if (type === LibType.panoVideo) {
    for (let i = 0; i < items.length; i++) {
      const x = await getLibPVideoData(items[i].id);
      originTrackList[i] = x.originTrack;
    }
  }

  const ids: string[] = [];
  $sceneMapApi.update((draft) => {
    for (let i = 0; i < items.length; i++) {
      const item = items[i] as LibItemScene;
      const id = nanoid();
      const thumbUrl =
        type === LibType.pano
          ? (item as LibItemPano).qiniuThumbUrl
          : (item as LibItemPVideo | LibItemMatrix).thumbUrl;
      const multires =
        type === LibType.panoVideo
          ? {}
          : (parseJsonString(
              (item as LibItemPano | LibItemMatrix).multires
            ) as PanoMultires);
      const path =
        type === LibType.panoVideo
          ? ''
          : (item as LibItemPano | LibItemMatrix).path;
      const transcodeTrack =
        type === LibType.panoVideo
          ? (parseJsonString(
              (item as LibItemPVideo).transcodeTrack
            ) as TranscodeTrack)
          : {};
      const matrixFov =
        type === LibType.matrix
          ? (parseJsonString((item as LibItemMatrix).fov) as MatrixFov)
          : {};
      const north =
        type === LibType.pano ? (item as LibItemPano).north ?? 0 : 0;
      const originTrack =
        type === LibType.panoVideo ? originTrackList[i] : undefined;
      draft.set(id, {
        id,
        tourId: tour.id!,
        type: getType(type),
        materialId: item.id,
        name: item.name,
        thumbUrl,
        createDate: item.createDate,
        updateDate: item.updateDate,
        categoryId: cId,
        showThumb: 1,
        multires,
        cdnDomain: item.cdnDomain,
        path,
        view: viewDefault,
        tid: '',
        transcodeTrack,
        matrixFov,
        sort: 0,
        sound: {},
        voice: {},
        video: [],
        mask: {},
        north,
        sandBox: { list: [] },
        config: {},
        originTrack,
      });
      ids.push(id);
    }
  });
  const cId = selectedPath.categoryPath[selectedPath.categoryPath.length - 1];
  $categoryMapApi.update((draft) => {
    // 如果传入了目标分组, 则添加到那个分组, 否则添加到当前分组
    const item = draft.get(targetCategory || cId);
    if (!item) return;
    item.sceneIds = [...item.sceneIds, ...ids];
  });
  // (如果没传目标分组 || 传入的目标分组正好是当前分组), 并且当前分组没场景, 才触发
  if ((!targetCategory || targetCategory === cId) && !selectedPath.id) {
    $selectedPathApi.update((draft) => {
      draft.id = ids[0] ?? null;
    });
  }
});

export default addScenes;
